import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services';
import {
  LayoutDashboard,
  Users,
  MapPin,
  UserCheck,
  CalendarDays,
  Wallet,
  Settings,
  Building2,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Clock,
  FileText,
  UserPlus,
  Bell,
  HelpCircle,
  Star,
  ClipboardList,
  KeyRound,
  User,
  UsersRound,
  UserCog,
  Navigation,
  ShieldCheck,
  CircleDollarSign,
  UserMinus,
  Mail,
  Briefcase,
  Receipt,
  GraduationCap,
  Smile,
  Target,
  FileCheck,
  Trophy,
  Package,
  FileSpreadsheet,
  Coins,
  FileSignature,
  Megaphone,
  ScrollText,
  Gift
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasReportees, setHasReportees] = useState(false);

  // Check if the current user has reportees (is a reporting manager)
  useEffect(() => {
    const checkReportees = async () => {
      if (user?.role === 'employee' || user?.role === 'marketing') {
        try {
          const res = await userAPI.getMyReportees();
          const reportees = res?.data?.reportees || res?.data?.employees || [];
          if (reportees.length > 0) {
            setHasReportees(true);
          }
        } catch {
          // Not a reporting manager or error - hide team section
        }
      }
    };
    checkReportees();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define menu items based on user role and client module access
  const getMenuItems = () => {
    const baseUrl = '/dashboard';
    
    // Get client modules (for admin and employee roles)
    const clientModules = user?.client?.subscription?.modules || {};
    
    if (user?.role === 'superadmin') {
      return [
        { path: `${baseUrl}`, label: 'Overview', icon: LayoutDashboard, exact: true },
        { path: `${baseUrl}/clients`, label: 'Clients', icon: Building2 },
        { path: `${baseUrl}/separations`, label: 'Separations', icon: UserMinus },
        { path: `${baseUrl}/expenditures`, label: 'Expenditures', icon: Wallet },
        { path: `${baseUrl}/profits`, label: 'Profits', icon: CircleDollarSign },
        { path: `${baseUrl}/quotations`, label: 'Quotations', icon: FileSignature },
        { path: `${baseUrl}/billing`, label: 'Billing', icon: Receipt },
        { path: `${baseUrl}/analytics`, label: 'Analytics', icon: BarChart3 },
        { path: `${baseUrl}/announcements`, label: 'Announcements', icon: Megaphone },
        { path: `${baseUrl}/audit-log`, label: 'Audit Log', icon: ScrollText },
        { path: `${baseUrl}/contact-submissions`, label: 'Contact Forms', icon: Mail },
        { path: `${baseUrl}/support`, label: 'Platform Support', icon: HelpCircle },
        { path: `${baseUrl}/staff`, label: 'Staff Management', icon: UsersRound },
        { path: `${baseUrl}/settings`, label: 'Settings', icon: Settings },
      ];
    }

    if (user?.role === 'platform_staff') {
      // DigiHR-internal staff: only show sidebar items for modules they can read.
      // Staff Management itself is deliberately superadmin-only, not shown here.
      const perms = user?.platformPermissions || {};
      const items = [{ path: `${baseUrl}`, label: 'Overview', icon: LayoutDashboard, exact: true }];
      if (perms.clients?.read) items.push({ path: `${baseUrl}/clients`, label: 'Clients', icon: Building2 });
      if (perms.separations?.read) items.push({ path: `${baseUrl}/separations`, label: 'Separations', icon: UserMinus });
      if (perms.expenditures?.read) items.push({ path: `${baseUrl}/expenditures`, label: 'Expenditures', icon: Wallet });
      if (perms.profits?.read) items.push({ path: `${baseUrl}/profits`, label: 'Profits', icon: CircleDollarSign });
      if (perms.quotations?.read) items.push({ path: `${baseUrl}/quotations`, label: 'Quotations', icon: FileSignature });
      if (perms.billing?.read) items.push({ path: `${baseUrl}/billing`, label: 'Billing', icon: Receipt });
      if (perms.analytics?.read) items.push({ path: `${baseUrl}/analytics`, label: 'Analytics', icon: BarChart3 });
      if (perms.announcements?.read) items.push({ path: `${baseUrl}/announcements`, label: 'Announcements', icon: Megaphone });
      if (perms.audit?.read) items.push({ path: `${baseUrl}/audit-log`, label: 'Audit Log', icon: ScrollText });
      if (perms.contactForms?.read) items.push({ path: `${baseUrl}/contact-submissions`, label: 'Contact Forms', icon: Mail });
      if (perms.platformSupport?.read) items.push({ path: `${baseUrl}/support`, label: 'Platform Support', icon: HelpCircle });
      if (perms.settings?.read) items.push({ path: `${baseUrl}/settings`, label: 'Settings', icon: Settings });
      return items;
    }
    
    if (user?.role === 'admin') {
      // Build menu items based on enabled modules
      const items = [
        { path: `${baseUrl}`, label: 'Overview', icon: LayoutDashboard, exact: true },
      ];
      
      // Check each module and add to menu if enabled
      if (clientModules.employees !== false) {
        items.push({ path: `${baseUrl}/employees`, label: 'Employees', icon: Users });
      }
      if (clientModules.customers !== false) {
        items.push({ path: `${baseUrl}/customers`, label: 'Customers', icon: UserCheck });
      }
      if (clientModules.locations !== false) {
        items.push({ path: `${baseUrl}/locations`, label: 'Locations', icon: MapPin });
      }
      if (clientModules.holidays !== false) {
        items.push({ path: `${baseUrl}/holidays`, label: 'Holidays', icon: Star });
      }
      if (clientModules.leaves !== false) {
        items.push({ path: `${baseUrl}/leaves`, label: 'Leave Management', icon: CalendarDays });
      }
      // Regularization is always available for admin
      items.push({ path: `${baseUrl}/regularization`, label: 'Regularization', icon: ClipboardList });
      
      // Location Tracker (for attendance module)
      if (clientModules.attendance !== false) {
        items.push({ path: `${baseUrl}/location-tracker`, label: 'Location Tracker', icon: Navigation });
      }
      
      if (clientModules.payroll !== false) {
        items.push({ path: `${baseUrl}/payroll`, label: 'Payroll', icon: Wallet });
        items.push({ path: `${baseUrl}/pf-ecr`, label: 'PF ECR', icon: FileSpreadsheet });
        items.push({ path: `${baseUrl}/bonus`, label: 'Statutory Bonus', icon: Gift });
      }

      if (clientModules.expenses === true) {
        items.push({ path: `${baseUrl}/expenses`, label: 'Expenses', icon: Receipt });
      }

      if (clientModules.lnd === true) {
        items.push({ path: `${baseUrl}/lnd`, label: 'L&D', icon: GraduationCap });
      }

      if (clientModules.moodCheckin === true) {
        items.push({ path: `${baseUrl}/mood`, label: 'Mood Analytics', icon: Smile });
      }

      if (clientModules.pms === true) {
        items.push({ path: `${baseUrl}/pms`, label: 'Performance', icon: Target });
      }

      if (clientModules.policies === true) {
        items.push({ path: `${baseUrl}/policies`, label: 'Policies', icon: FileCheck });
      }

      if (clientModules.rewards === true) {
        items.push({ path: `${baseUrl}/rewards`, label: 'Rewards & Recognition', icon: Trophy });
      }

      if (clientModules.assets === true) {
        items.push({ path: `${baseUrl}/assets`, label: 'Asset Management', icon: Package });
      }

      if (clientModules.leaveEncashment === true) {
        items.push({ path: `${baseUrl}/encashment/config`, label: 'Encashment Config', icon: Coins });
        items.push({ path: `${baseUrl}/encashment/requests`, label: 'Encashment Requests', icon: Coins });
      }

      // Advances/Loans - always available for admin
      items.push({ path: `${baseUrl}/advances`, label: 'Advance Management', icon: CircleDollarSign });
      
      // Letters is always available for admin
      items.push({ path: `${baseUrl}/letters`, label: 'Letters', icon: FileText });
      
      // Resignations: check if module is enabled
      if (clientModules.resignation !== false) {
        items.push({ path: `${baseUrl}/resignations`, label: 'Resignations', icon: UserMinus });
      }

      // Support Centre - always available for admin (internal HR tickets + DigiHR platform issues)
      items.push({ path: `${baseUrl}/support`, label: 'Support Centre', icon: HelpCircle });

      // Talent Acquisition: check if module is enabled
      if (clientModules.talentAcquisition === true) {
        items.push({ path: `${baseUrl}/recruitment`, label: 'Recruitment', icon: Briefcase });
        items.push({ path: `${baseUrl}/recruitment/offers`, label: 'CTC & Offers', icon: Briefcase });
      }

      // Settings is always available
      items.push({ path: `${baseUrl}/settings`, label: 'Settings', icon: Settings });
      
      return items;
    }
    
    // Employee/Marketing role
    const items = [
      { path: `${baseUrl}`, label: 'Overview', icon: LayoutDashboard, exact: true },
    ];
    
    // Check each module and add to menu if enabled
    if (clientModules.attendance !== false) {
      items.push({ path: `${baseUrl}/attendance`, label: 'Attendance', icon: Clock });
      items.push({ path: `${baseUrl}/regularization`, label: 'Regularization', icon: ClipboardList });
    }
    if (clientModules.customers !== false && (user?.role === 'marketing' || user?.permissions?.canManageCustomers === true)) {
      items.push({ path: `${baseUrl}/customers`, label: 'Customers', icon: UserCheck });
    }
    if (clientModules.leaves !== false) {
      items.push({ path: `${baseUrl}/leaves`, label: 'Leaves', icon: CalendarDays });
    }
    if (clientModules.payroll !== false) {
      items.push({ path: `${baseUrl}/payroll`, label: 'Payroll', icon: Wallet });
      if (user?.permissions?.canGeneratePayroll === true) {
        items.push({ path: `${baseUrl}/pf-ecr`, label: 'PF ECR', icon: FileSpreadsheet });
        items.push({ path: `${baseUrl}/bonus`, label: 'Statutory Bonus', icon: Gift });
      }
    }

    if (clientModules.expenses === true) {
      items.push({ path: `${baseUrl}/my-expenses`, label: 'Expenses', icon: Receipt });
    }

    if (clientModules.lnd === true) {
      items.push({ path: `${baseUrl}/my-learning`, label: 'My Learning', icon: GraduationCap });
    }

    if (clientModules.moodCheckin === true && user?.permissions?.canViewMoodAnalytics === true) {
      items.push({ path: `${baseUrl}/mood`, label: 'Mood Analytics', icon: Smile });
    }

    if (clientModules.pms === true) {
      items.push({ path: `${baseUrl}/my-goals`, label: 'My Goals', icon: Target });
    }

    if (clientModules.policies === true) {
      items.push({ path: `${baseUrl}/my-policies`, label: 'Policies', icon: FileCheck });
    }

    if (clientModules.rewards === true) {
      items.push({ path: `${baseUrl}/my-rewards`, label: 'My Rewards', icon: Trophy });
    }

    if (clientModules.assets === true) {
      items.push({ path: `${baseUrl}/my-assets`, label: 'My Assets', icon: Package });
    }

    if (clientModules.leaveEncashment === true && user?.permissions?.canManageEncashment === true) {
      items.push({ path: `${baseUrl}/encashment/config`, label: 'Encashment Config', icon: Coins });
      items.push({ path: `${baseUrl}/encashment/requests`, label: 'Encashment Requests', icon: Coins });
    }

    // Advances/Loans - always available for employees
    items.push({ path: `${baseUrl}/request-advance`, label: 'Request Advance', icon: CircleDollarSign });
    items.push({ path: `${baseUrl}/my-advances`, label: 'My Advances', icon: CircleDollarSign });
    
    // Show Advance Management for users with permission
    if (user?.permissions?.canManageAdvances === true) {
      items.push({ path: `${baseUrl}/advances`, label: 'Advance Management', icon: CircleDollarSign });
    }
    
    // Letters is always available for employees
    items.push({ path: `${baseUrl}/letters`, label: 'Letters', icon: FileText });
    
    // Resignation: check if module is enabled
    if (clientModules.resignation !== false) {
      items.push({ path: `${baseUrl}/resignation`, label: 'Resignation', icon: UserMinus });
    }
    
    // Talent Acquisition: check if module is enabled
    // Show for users with TA roles OR employees assigned as HOD of departments with job request permission
    // OR employees who have jobs assigned to them
    const hasTARole = ['hod', 'hr_head', 'ta_executive'].includes(user?.role);
    const isAssignedHOD = user?.isHOD === true; // Set by backend if user is HOD of any department
    const hasAssignedJobs = user?.hasAssignedJobs === true; // Set by backend if user has jobs assigned
    if (clientModules.talentAcquisition === true && (hasTARole || isAssignedHOD || hasAssignedJobs)) {
      items.push({ path: `${baseUrl}/recruitment`, label: 'Recruitment', icon: Briefcase });
    }
    if (clientModules.talentAcquisition === true && user?.role === 'hr_head') {
      items.push({ path: `${baseUrl}/recruitment/offers`, label: 'CTC & Offers', icon: Briefcase });
    }
    if (clientModules.talentAcquisition === true && ['manager', 'hod'].includes(user?.role)) {
      items.push({ path: `${baseUrl}/recruitment/interviews`, label: 'My Interviews', icon: Briefcase });
    }

    // Support Centre - always available (internal HR tickets for everyone)
    items.push({ path: `${baseUrl}/support`, label: 'Support Centre', icon: HelpCircle });

    // My Profile is always available for employees
    items.push({ path: `${baseUrl}/my-profile`, label: 'My Profile', icon: User });
    
    return items;
  };

  // Get team menu items for reporting managers
  const getTeamMenuItems = () => {
    if (!hasReportees) return [];
    const baseUrl = '/dashboard';
    const clientModules = user?.client?.subscription?.modules || {};
    
    const items = [
      { path: `${baseUrl}/team-members`, label: 'My Team', icon: UsersRound },
      { path: `${baseUrl}/team-attendance`, label: 'Team Attendance', icon: Clock },
      { path: `${baseUrl}/team-leaves`, label: 'Team Leaves', icon: CalendarDays },
      { path: `${baseUrl}/team-regularization`, label: 'Team Regularization', icon: ClipboardList },
    ];
    
    // Only add team resignations if module is enabled
    if (clientModules.resignation !== false) {
      items.push({ path: `${baseUrl}/team-resignations`, label: 'Team Resignations', icon: UserMinus });
    }
    
    return items;
  };

  const menuItems = getMenuItems();
  const teamMenuItems = getTeamMenuItems();

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path) && path !== '/dashboard';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'manager': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          bg-white border-r border-gray-200 shadow-sm
          transform transition-all duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64 h-screen flex flex-col
        `}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <Link to="/dashboard" className="flex items-center">
  
                <img 
                  src="/logo.png" 
                  alt="DIGIHR" 
                  className="h-12 object-contain"
                />
             
            </Link>
          )}
          {sidebarCollapsed && (
            user?.role === 'superadmin' ? (
              <img 
                src="/logo.png" 
                alt="DIGIHR" 
                className="w-12 h-12 object-contain mx-auto"
              />
            ) : user?.client?.logo?.url ? (
              <img 
                src={user.client.logo.url} 
                alt={user.client.name || 'Organization'} 
                className="w-12 h-12 object-contain mx-auto"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl mx-auto">
                {user?.client?.name?.charAt(0)?.toUpperCase() || 'O'}
              </div>
            )
          )}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200
                      ${active 
                        ? 'bg-blue-50 text-blue-700 font-medium shadow-sm' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-600' : ''}`} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Team Section for Reporting Managers */}
          {teamMenuItems.length > 0 && (
            <>
              <div className={`mx-3 my-3 border-t border-gray-200`} />
              {!sidebarCollapsed && (
                <div className="px-4 mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">My Team</span>
                </div>
              )}
              {sidebarCollapsed && (
                <div className="flex justify-center mb-2">
                  <ShieldCheck className="w-4 h-4 text-gray-400" />
                </div>
              )}
              <ul className="space-y-1 px-3">
                {teamMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path, item.exact);
                  
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg
                          transition-all duration-200
                          ${active 
                            ? 'bg-emerald-50 text-emerald-700 font-medium shadow-sm' 
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }
                          ${sidebarCollapsed ? 'justify-center' : ''}
                        `}
                        title={sidebarCollapsed ? item.label : ''}
                      >
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-emerald-600' : ''}`} />
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>

        {/* Collapse Toggle, Change Password & Logout */}
        <div className="p-3 border-t border-gray-200 space-y-2 flex-shrink-0">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
          <Link
            to="/dashboard/change-password"
            onClick={() => setMobileMenuOpen(false)}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-gray-600 hover:bg-gray-100 transition-colors
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
            title={sidebarCollapsed ? 'Change Password' : ''}
          >
            <KeyRound className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium">Change Password</span>}
          </Link>
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-red-600 hover:bg-red-50 transition-colors
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {[...menuItems, ...teamMenuItems].find(item => isActive(item.path, item.exact))?.label || 'Dashboard'}
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Organization Info for Client/Employee */}
            {user?.role !== 'superadmin' && user?.client && (
              <div className="hidden md:flex items-center gap-3 pr-3 border-r border-gray-200">
                {user.client.logo?.url ? (
                  <img 
                    src={user.client.logo.url} 
                    alt={user.client.name} 
                    className="h-10 w-10 object-contain rounded-lg border border-gray-200 p-1"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {user.client.name?.charAt(0)?.toUpperCase() || 'O'}
                  </div>
                )}
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900 max-w-[150px] truncate">{user.client.name}</p>
                  <p className="text-xs text-gray-500">{user.client.code}</p>
                </div>
              </div>
            )}
            
            {/* User Info */}
            <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-gray-200">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </span>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-md">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {children}
        </main>
      </div>

    </div>
  );
};

export default DashboardLayout;
